import { useMemo, useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import SEO from "../components/site/SEO";
import { CTASection, InfoCard, PageHero, SectionHeader } from "../components/site/Sections";
import { buildWhatsAppUrl, companyInfo, imageLibrary } from "../content/siteContent";

const defaultForm = {
  name: "",
  company: "",
  phone: "",
  message: "",
};

const ContactPage = () => {
  const [form, setForm] = useState(defaultForm);
  const [submitted, setSubmitted] = useState(false);

  const encodedMessage = useMemo(
    () => {
      const formattedMessage = [
        "Olá! Gostaria de solicitar um orçamento com a W-Electric.",
        "",
        "*DADOS DO CONTATO*",
        `*Nome:* ${form.name || "-"}`,
        `*Empresa / Condomínio:* ${form.company || "-"}`,
        `*Telefone:* ${form.phone || "-"}`,
        "",
        "*DETALHES DA SOLICITAÇÃO*",
        `${form.message || "-"}`,
      ].join("\n");

      return buildWhatsAppUrl(formattedMessage);
    },
    [form.company, form.message, form.name, form.phone]
  );

  const handleChange = ({ target }) => {
    const { name, value } = target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitted(true);
    window.open(encodedMessage, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <SEO
        title="Contato | W-Electric Engenharia Elétrica"
        description="Entre em contato com a W-Electric Engenharia Elétrica por telefone, e-mail ou WhatsApp para solicitar orçamento em São Paulo."
        canonicalPath="/contato"
        keywords={[
          "contato W-Electric",
          "orçamento engenharia elétrica",
          "WhatsApp manutenção elétrica São Paulo",
        ]}
      />

      <PageHero
        eyebrow="Contato"
        title="Fale com a W-Electric Engenharia Elétrica"
        description="Atendimento técnico para condomínios, indústrias e empresas que precisam de manutenção elétrica, ensaios e comissionamento em São Paulo."
        image={imageLibrary.hero}
        ctaMessage="Olá, gostaria de solicitar um orçamento com a W-Electric."
        testId="contact-hero"
      />

      <section className="container-shell py-14 md:py-20" data-testid="contact-main-section">
        <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <div className="grid gap-6">
            <SectionHeader
              eyebrow="Canais de contato"
              title="Atendimento direto e objetivo"
              description="Escolha o melhor canal para iniciar sua solicitação de orçamento ou suporte técnico."
              testId="contact-header"
            />

            <div className="surface-card p-6 md:p-8" data-testid="contact-direct-card">
              <div className="grid gap-5 text-sm text-slate-700">
                <a href={companyInfo.phoneHref} className="flex items-center gap-3" data-testid="contact-phone-link">
                  <Phone className="h-5 w-5 text-safety" />
                  <span data-testid="contact-phone-text">{companyInfo.phoneDisplay}</span>
                </a>
                <a href={companyInfo.emailHref} className="flex items-center gap-3" data-testid="contact-email-link">
                  <Mail className="h-5 w-5 text-safety" />
                  <span data-testid="contact-email-text">{companyInfo.email}</span>
                </a>
                <div className="flex items-center gap-3" data-testid="contact-city-row">
                  <MapPin className="h-5 w-5 text-safety" />
                  <span data-testid="contact-city-text">Atendimento em São Paulo</span>
                </div>
                <a
                  href={buildWhatsAppUrl("Olá, gostaria de solicitar um orçamento com a W-Electric.")}
                  target="_blank"
                  rel="noreferrer"
                  className="primary-button mt-3 justify-center"
                  data-testid="contact-whatsapp-button"
                >
                  SOLICITAR ORÇAMENTO
                </a>
              </div>
            </div>

            <InfoCard
              title="Resposta mais ágil"
              description="Envie uma mensagem com tipo de instalação, cidade, urgência e escopo desejado para receber um atendimento mais preciso."
              items={[
                "Tipo de instalação ou ativo",
                "Cidade e disponibilidade de visita",
                "Objetivo da manutenção ou ensaio",
              ]}
              testId="contact-response-card"
            />
          </div>

          <div className="surface-card p-6 md:p-8" data-testid="contact-form-card">
            <h2 className="font-heading text-4xl uppercase text-brand" data-testid="contact-form-title">
              Solicite um orçamento
            </h2>
            <p className="mt-4 body-copy" data-testid="contact-form-description">
              Preencha os dados abaixo. Ao enviar, abriremos o WhatsApp com sua mensagem já formatada para agilizar o atendimento.
            </p>

            <form className="mt-8 grid gap-5" onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid gap-2">
                <label className="text-sm font-semibold uppercase tracking-[0.2em] text-brand" htmlFor="name" data-testid="contact-form-name-label">
                  Nome
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  className="h-12 border border-slate-300 px-4 outline-none transition focus:border-safety"
                  placeholder="Seu nome"
                  required
                  data-testid="contact-form-name-input"
                />
              </div>

              <div className="grid gap-2 md:grid-cols-2 md:gap-5">
                <div className="grid gap-2">
                  <label className="text-sm font-semibold uppercase tracking-[0.2em] text-brand" htmlFor="company" data-testid="contact-form-company-label">
                    Empresa / condomínio
                  </label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={handleChange}
                    className="h-12 border border-slate-300 px-4 outline-none transition focus:border-safety"
                    placeholder="Nome da empresa ou condomínio"
                    data-testid="contact-form-company-input"
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-semibold uppercase tracking-[0.2em] text-brand" htmlFor="phone" data-testid="contact-form-phone-label">
                    Telefone
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    className="h-12 border border-slate-300 px-4 outline-none transition focus:border-safety"
                    placeholder="(11) 99999-9999"
                    required
                    data-testid="contact-form-phone-input"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <label className="text-sm font-semibold uppercase tracking-[0.2em] text-brand" htmlFor="message" data-testid="contact-form-message-label">
                  Mensagem
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows="6"
                  className="border border-slate-300 px-4 py-3 outline-none transition focus:border-safety"
                  placeholder="Descreva brevemente o serviço desejado"
                  required
                  data-testid="contact-form-message-input"
                />
              </div>

              <button type="submit" className="primary-button justify-center" data-testid="contact-form-submit-button">
                SOLICITAR ORÇAMENTO
              </button>

              <p className="text-sm text-slate-500" data-testid="contact-form-helper-text">
                Os dados preenchidos serão enviados ao WhatsApp em mensagem formatada.
              </p>

              {submitted ? (
                <div className="border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="contact-form-success-message">
                  Seu pedido foi preparado no WhatsApp com a mensagem formatada. Se a nova aba não abrir, use o botão de orçamento acima.
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </section>

      <CTASection
        title="Precisa de resposta rápida para um ativo crítico?"
        description="Acione a W-Electric pelo WhatsApp e envie as informações da sua instalação para acelerar a análise técnica."
        buttonLabel="Abrir WhatsApp"
        buttonMessage="Olá, preciso de atendimento rápido para um ativo elétrico crítico."
        testId="contact-cta"
      />
    </>
  );
};

export default ContactPage;